import { useId } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import Input from '../../../../components/ui/Input';
import Badge from '../../../../components/ui/Badge';
import PassFailToggle from '../PassFailToggle';

/**
 * Mobile RCD test card — push-button result + trip time.
 *
 * Public interface: circuit identity + controlled `pushResult` / `tripTimeMs`
 * (+ optional `onEdit`, `tested`, `isSynced`). Hides status chrome
 * (same as DefaultTestCard), the two-column push-test / trip-time layout, and
 * the saved/read-only + sync-badge state once a result has been recorded.
 * Failure reason lives in the Comment / Edit popup, not on the card.
 *
 * Props:
 *   circuitLabel — primary title (e.g. "DB.1 – CB2")
 *   make, model, rating — joined as "MAKE / MODEL · RATING"
 *   pushResult — `'pass' | 'fail' | null`
 *   onPushResultChange — (next) => void
 *   tripTimeMs — controlled trip-time value (string or number)
 *   onTripTimeChange — (next: string) => void
 *   tested — true once a result is saved for this asset; locks the inputs
 *   isSynced — when `tested`, whether the save is confirmed ("Saving…" if false)
 *   onEdit — optional edit handler
 *   disabled — disables controls regardless of `tested`
 *   className — extra class(es) on the card
 */
export default function MobileTestCardRcd({
  circuitLabel,
  make,
  model,
  rating,
  pushResult = null,
  onPushResultChange,
  tripTimeMs = '',
  onTripTimeChange,
  tested = false,
  isSynced = true,
  onEdit,
  disabled = false,
  className = '',
  ...props
}) {
  const tripId = useId();
  const state = pushResult === 'pass' || pushResult === 'fail' ? pushResult : 'unset';
  const details = [make, model].filter(Boolean).join(' / ');
  const detailLine = [details, rating].filter(Boolean).join(' · ');
  const cardStatus = pushResult === 'pass' ? 'success' : pushResult === 'fail' ? 'danger' : undefined;
  const locked = disabled || tested;
  const tripLocked = locked || pushResult === 'fail';

  return (
    <article
      className={[
        'mobile-test-card',
        'mobile-test-card--rcd',
        cardStatus ? `card--${cardStatus}` : '',
        className,
      ].filter(Boolean).join(' ')}
      data-state={state}
      {...props}
    >
      <div className="mobile-test-card__header">
        <div className="mobile-test-card__lead">
          <span className="mobile-test-card__sideline" aria-hidden="true" />
          <div className="mobile-test-card__identity">
            <div className="mobile-test-card__code">{circuitLabel}</div>
            {detailLine ? (
              <div className="mobile-test-card__details">{detailLine}</div>
            ) : null}
          </div>
        </div>

        <div className="mobile-test-card__meta">
          {tested ? (
            <Badge variant={!isSynced ? 'warning' : pushResult === 'fail' ? 'danger' : 'success'}>
              {!isSynced ? 'Saving…' : pushResult === 'fail' ? 'FAILED' : 'PASSED'}
            </Badge>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className="mobile-test-card__edit"
              onClick={onEdit}
              disabled={disabled}
              aria-label="Edit RCD"
              title="Edit"
            >
              <FiEdit2 size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <div className="mobile-test-card-rcd__fields">
        <span className="mobile-test-card-rcd__field-label">Push test</span>
        <label className="mobile-test-card-rcd__field-label" htmlFor={tripId}>
          Trip time
        </label>

        <PassFailToggle
          value={pushResult}
          onChange={onPushResultChange}
          disabled={locked}
          ariaLabel={`Push test for ${circuitLabel}`}
          className="mobile-test-card-rcd__control"
        />

        <div className="mobile-test-card-rcd__trip-row">
          <Input
            id={tripId}
            type="text"
            inputMode="numeric"
            placeholder={pushResult === 'fail' ? 'N/A' : '0'}
            value={tripTimeMs}
            disabled={tripLocked}
            onChange={(e) => onTripTimeChange?.(e.target.value)}
            className="mobile-test-card-rcd__trip-input"
          />
          <span className="mobile-test-card-rcd__unit" aria-hidden="true">ms</span>
        </div>
      </div>
    </article>
  );
}
